import React, { useEffect, useRef } from "react";
import cx from "classnames";
import routeWithUserSession from "../../components/application-area/routeWithUserSession";
import Link from "@/components/Link";
import PageHeader from "@/components/page-header";
import Paginator from "@/components/items-list/components/Paginator";
import DynamicComponent from "@/components/DynamicComponent";

import { wrap as itemsList, ControllerType } from "@/components/items-list/ItemsList";
import useItemsListExtraActions from "@/components/items-list/hooks/useItemsListExtraActions";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { UrlStateStorage } from "@/components/items-list/classes/StateStorage";

import * as Sidebar from "@/components/items-list/components/Sidebar";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";

import { toUpper } from "lodash";

import Layout from "@/components/layouts/ContentWithSidebar";

import { Indexer } from "@/services/indexer";
import { currentUser } from "@/services/auth";
import location from "@/services/location";
import routes from "@/services/routes";

import IndexerListEmptyState from "./IndexerListEmptyState";

import "./indexers-list.css";
import { IndexerTagsControl } from "@/components/tags-control/TagsControl";

export const STATE_CLASS = {
  unknown: "label-warning",
  ok: "label-success",
  triggered: "label-danger",
};

const sidebarMenu = [
  {
    key: "all",
    href: "indexers",
    title: "All Indexers",
    icon: () => <Sidebar.MenuIcon icon="fa fa-cubes" />,
  },
  {
    key: "my",
    href: "indexers/my",
    title: "My Indexers",
    icon: () => <Sidebar.ProfileImage user={currentUser} />,
  },
  {
    key: "favorites",
    href: "indexers/favorites",
    title: "Favorites",
    icon: () => <Sidebar.MenuIcon icon="fa fa-star" />,
  },
  {
    key: "archive",
    href: "indexers/archive",
    title: "Archived",
    icon: () => <Sidebar.MenuIcon icon="fa fa-archive" />,
  },
];

const listColumns = [
  Columns.favorites({ className: "p-r-0" }),
  Columns.custom.sortable(
    (text, indexer) => (
      <React.Fragment>
          <Link className="table-main-title" href={"indexers/" + indexer.id }>
            {indexer.name}
          </Link>
          <IndexerTagsControl className="d-block" tags={indexer.tags} isArchived={indexer.is_archived} />
        </React.Fragment>
    ),
    {
      title: "Name",
      field: "name",
    }
  ),
  Columns.custom.sortable(
    (text, indexer) => (
      <div>
        {indexer.query 
          ? <>
              {
                new Intl.DateTimeFormat('en-GB', {
                  year: '2-digit', 
                  month: '2-digit', 
                  day: '2-digit', 
                  hour: '2-digit', 
                  minute: '2-digit'
                }).format(new Date(indexer.query.retrieved_at)).replace(/,/g, '')
              }
            </>
          : 'No query'}
      </div>
    ),
    {
      title: "Last Retrieved At",
      field: "query.retrieved_at",
      width: "1%"
    }
  ),
  Columns.custom.sortable(
    (text, indexer) => (
      <div>
        {indexer.query 
          ? <Link href={"queries/" + indexer.query.id}>{indexer.query.name}</Link>
          : 'No query'}
      </div>
    ),
    {
      title: "Query",
      field: "query.name",
      width: "1%"
      
    }
  ),
  Columns.custom.sortable(
    (text, indexer) => (
      <div>
        {indexer.data_source 
          ? <Link href={"data_sources/" + indexer.data_source.id}>{indexer.data_source.name}</Link>
          : 'No data source'}
      </div>
    ),
    {
      title: "Target Data Source",
      field: "data_source.name",
      width: "1%"
    }
  ),
  Columns.custom((text, item) => item.user.name, { title: "Created By", width: "1%" }),
  Columns.dateTime.sortable({ title: "Created At", field: "created_at", width: "1%" }),
  Columns.dateTime.sortable({ title: "Last Updated At", field: "updated_at", width: "1%" }),
  Columns.dateTime.sortable({ title: "Last Triggered At", field: "last_triggered_at", width: "1%" }),
];

function IndexersListExtraActions(props) {
  return <DynamicComponent name="IndexersList.Actions" {...props} />;
}

function IndexersList({ controller }) {
  const controllerRef = useRef();
  controllerRef.current = controller;

  useEffect(() => {
    const unlistenLocationChanges = location.listen((unused, action) => {
      const searchTerm = location.search.q || "";
      if (action === "PUSH" && searchTerm !== controllerRef.current.searchTerm) {
        controllerRef.current.updateSearch(searchTerm);
      }
    });

    return () => {
      unlistenLocationChanges();
    };
  }, []);

  const {
    areExtraActionsAvailable,
    listColumns: tableColumns,
    Component: ExtraActionsComponent,
    selectedItems,
  } = useItemsListExtraActions(controller, listColumns, IndexersListExtraActions);

  return (
    <div className="page-indexers-list">
      <div className="container">
        <PageHeader
          title={controller.params.pageTitle}
          actions={
            currentUser.hasPermission("list_indexers") ? (
              <Link.Button block type="primary" href="indexers/new">
                <i className="fa fa-plus m-r-5" aria-hidden="true" />
                New Indexer
              </Link.Button>
            ) : null
          }
        />
        <Layout>
          <Layout.Sidebar className="m-b-0">
            <Sidebar.SearchInput
              placeholder="Search Indexers..."
              label="Search indexers"
              value={controller.searchTerm}
              onChange={controller.updateSearch}
            />
            <Sidebar.Menu items={sidebarMenu} selected={controller.params.currentPage} />
            <Sidebar.Tags url="api/indexers/tags" onChange={controller.updateSelectedTags} showUnselectAll />
          </Layout.Sidebar>
          <Layout.Content>
            {controller.isLoaded && controller.isEmpty ? (
              <IndexerListEmptyState
                page={controller.params.currentPage}
                searchTerm={controller.searchTerm}
                selectedTags={controller.selectedTags}
              />
            ) : (
              <React.Fragment>
                <div className={cx({ "m-b-10": areExtraActionsAvailable })}>
                  <ExtraActionsComponent selectedItems={selectedItems} />
                </div>
                <div className="bg-white tiled table-responsive">
                  <ItemsTable
                    items={controller.pageItems}
                    loading={!controller.isLoaded}
                    columns={tableColumns}
                    orderByField={controller.orderByField}
                    orderByReverse={controller.orderByReverse}
                    toggleSorting={controller.toggleSorting}
                  />
                  <Paginator
                    showPageSizeSelect
                    totalCount={controller.totalItemsCount}
                    pageSize={controller.itemsPerPage}
                    onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
                    page={controller.page}
                    onChange={page => controller.updatePagination({ page })}
                  />
                </div>
              </React.Fragment>
            )}
          </Layout.Content>
        </Layout>
      </div>
    </div>
  );
}

IndexersList.propTypes = {
  controller: ControllerType.isRequired,
};

const IndexersListPage = itemsList(
  IndexersList,
  () =>
    new ResourceItemsSource({
      getResource({ params: { currentPage } }) {
        return {
          all: Indexer.query.bind(Indexer),
          my: Indexer.myIndexers.bind(Indexer),
          favorites: Indexer.favorites.bind(Indexer),
          archive: Indexer.archive.bind(Indexer),
        }[currentPage];
      },
      getItemProcessor() {
        return item => new Indexer(item);
      },
    }),
  () => new UrlStateStorage({ orderByField: "created_at", orderByReverse: true, itemsPerPage: 100 })
);

routes.register(
  "Indexers.List",
  routeWithUserSession({
    path: "/indexers",
    title: "Indexers",
    render: pageProps => <IndexersListPage {...pageProps} currentPage="all" />,
  })
);
routes.register(
  "Indexers.Favorites",
  routeWithUserSession({
    path: "/indexers/favorites",
    title: "Favorite Indexers",
    render: pageProps => <IndexersListPage {...pageProps} currentPage="favorites" />,
  })
);
routes.register(
  "Indexers.Archive",
  routeWithUserSession({
    path: "/indexers/archive",
    title: "Archived Indexers",
    render: pageProps => <IndexersListPage {...pageProps} currentPage="archive" />,
  })
);
routes.register(
  "Indexers.My",
  routeWithUserSession({
    path: "/indexers/my",
    title: "My Indexers",
    render: pageProps => <IndexersListPage {...pageProps} currentPage="my" />,
  })
);
